import { formattingLocale } from "../i18n/formattingLocale";
import { safeCrowdfundingVideoUrl } from "./crowdfundingVideoUrl";
import api from "../api";
import { fictionalCampaignPresentation } from "./crowdfundingPresentation";

const DEFAULT_PROJECT_IMAGE = "/images/social/eternua-crowdfunding.jpg";

const STATUS_META = Object.freeze({
  unknown: { label: "Statut non communiqué", tone: "neutral", open: false },
  draft: { label: "Brouillon", tone: "neutral", open: false },
  pending_review: { label: "En validation", tone: "warning", open: false },
  changes_requested: { label: "À compléter", tone: "warning", open: false },
  approved: { label: "Approuvé", tone: "info", open: false },
  scheduled: { label: "Bientôt ouvert", tone: "info", open: false },
  live: { label: "Campagne ouverte", tone: "live", open: true },
  active: { label: "Campagne ouverte", tone: "live", open: true },
  paused: { label: "Campagne suspendue", tone: "warning", open: false },
  suspended: { label: "Campagne suspendue", tone: "warning", open: false },
  succeeded: { label: "Objectif atteint", tone: "success", open: false },
  successful: { label: "Objectif atteint", tone: "success", open: false },
  failed: { label: "Campagne terminée", tone: "neutral", open: false },
  unsuccessful: { label: "Objectif non atteint", tone: "neutral", open: false },
  closing: { label: "Clôture en cours", tone: "warning", open: false },
  refunding: { label: "Remboursements en cours", tone: "warning", open: false },
  refunded: { label: "Contributions remboursées", tone: "neutral", open: false },
  payout_requested: { label: "Reversement demandé", tone: "info", open: false },
  payout_under_review: { label: "Reversement en vérification", tone: "info", open: false },
  payout_approved: { label: "Reversement approuvé", tone: "success", open: false },
  payout_processing: { label: "Reversement en traitement", tone: "info", open: false },
  paid_out: { label: "Reversement terminé", tone: "success", open: false },
  payout_failed: { label: "Échec du reversement", tone: "danger", open: false },
  disputed: { label: "Litige en cours", tone: "danger", open: false },
  canceled: { label: "Campagne annulée", tone: "danger", open: false },
  archived: { label: "Campagne archivée", tone: "neutral", open: false },
  refused: { label: "Projet refusé", tone: "danger", open: false },
});

export const CLOSED_CROWDFUNDING_CAPABILITIES = Object.freeze({
  environment: "SANDBOX",
  data_environment: "SANDBOX",
  data_is_synthetic: false,
  transactions_are_simulated: true,
  hub_enabled: false,
  transparency_enabled: false,
  campaign_read_enabled: false,
  campaign_submission_enabled: false,
  checkout_enabled: false,
  real_payments_enabled: false,
  refunds_enabled: false,
  payouts_enabled: false,
  prelaunch_enabled: false,
  account_follows_enabled: false,
  campaign_community_enabled: false,
  creator_transparency_writes_enabled: false,
  reward_fulfillment_enabled: false,
  campaign_analytics_enabled: false,
  stretch_goals_enabled: false,
  moderation_backoffice_enabled: false,
  private_messaging_enabled: false,
  advanced_fulfillment_enabled: false,
  discovery_search_enabled: false,
  advanced_analytics_enabled: false,
  abuse_protection_enabled: false,
  automatic_communications_enabled: false,
  legal_operations_enabled: false,
  service_health_enabled: false,
});

function asNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function safeImageUrl(value, fallback) {
  const candidate = String(value || "").trim();
  if (!candidate) return fallback;
  try {
    const origin = typeof window !== "undefined" && window.location?.origin && window.location.origin !== "null"
      ? window.location.origin
      : "https://eternua.invalid";
    const base = new URL(origin);
    const resolved = new URL(candidate, base);
    if (!["http:", "https:"].includes(resolved.protocol) || resolved.origin !== base.origin) return fallback;
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch (_error) {
    return fallback;
  }
}

function currencyFractionDigits(currency) {
  try {
    return new Intl.NumberFormat(formattingLocale(), {
      style: "currency",
      currency: String(currency || "EUR").toUpperCase(),
    }).resolvedOptions().maximumFractionDigits;
  } catch (_error) {
    return 2;
  }
}

function fromMinorUnits(value, currency) {
  return asNumber(value) / (10 ** currencyFractionDigits(currency));
}

function creatorInitials(name) {
  const initials = String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  return initials || "EP";
}

function unwrapCollection(payload) {
  if (Array.isArray(payload)) return payload;
  return asArray(payload?.results);
}

function normalizePairs(value, firstKey, secondKey) {
  return asArray(value).map((item) => {
    if (Array.isArray(item)) return [String(item[0] || ""), String(item[1] || "")];
    if (item && typeof item === "object") {
      return [String(item[firstKey] || ""), String(item[secondKey] || "")];
    }
    return ["", String(item || "")];
  }).filter((item) => item[0] || item[1]);
}

function daysUntil(value, status) {
  if (["succeeded", "successful", "failed", "unsuccessful", "refunded", "paid_out", "canceled", "archived"].includes(status)) return 0;
  const end = value ? new Date(value) : null;
  if (!end || Number.isNaN(end.getTime())) return null;
  return Math.max(0, Math.ceil((end.getTime() - Date.now()) / 86400000));
}

export function crowdfundingProgressPercent(project) {
  const basisPoints = Number(project?.progressBasisPoints);
  if (Number.isFinite(basisPoints)) return Math.max(0, basisPoints / 100);
  const raised = Number(project?.raised);
  const goal = Number(project?.goal);
  if (!Number.isFinite(raised) || !Number.isFinite(goal) || goal <= 0) return 0;
  return Math.max(0, (raised / goal) * 100);
}

export function crowdfundingDisplayProgress(project) {
  const exact = crowdfundingProgressPercent(project);
  const rounded = Math.round(exact * 10) / 10;
  return exact < 100 ? Math.min(99.9, rounded) : rounded;
}

export function getCrowdfundingStatus(status) {
  return STATUS_META[status] || { label: "Statut déclaré", tone: "neutral", open: false };
}

export function normalizeApiCampaign(campaign, index = 0) {
  const presentation = fictionalCampaignPresentation(campaign);
  const version = campaign?.current_version || {};
  const project = campaign?.project || {};
  const metrics = campaign?.metrics || {};
  const discovery = campaign?.discovery || {};
  const terms = version?.financial_terms && typeof version.financial_terms === "object"
    ? version.financial_terms
    : {};
  const statusKey = String(campaign?.status || "unknown").trim().toLowerCase();
  const statusMeta = getCrowdfundingStatus(statusKey);
  const creatorProvided = Boolean(campaign?.public_owner_name || terms.creator_display_name || terms.creator_name);
  const creator = campaign?.public_owner_name || terms.creator_display_name || terms.creator_name || "Porteur non communiqué";
  const currency = String(campaign?.currency || "EUR").toUpperCase();
  const permanent = campaign?.financing_mode === "keep_it_all";
  const goal = campaign?.goal_minor == null && permanent ? null : fromMinorUnits(campaign?.goal_minor, currency);
  const raised = fromMinorUnits(metrics?.eligible_amount_minor ?? metrics?.net_raised_minor, currency);
  const budget = asArray(version?.budget_lines).map((line) => [
    line.label || line.category || "Poste budgétaire",
    fromMinorUnits(line.planned_minor, currency),
  ]);
  const updates = asArray(campaign?.updates).map((item) => [
    item.published_at && Number.isFinite(new Date(item.published_at).getTime())
      ? new Intl.DateTimeFormat(formattingLocale(), { dateStyle: "long" }).format(new Date(item.published_at))
      : "Date non communiquée",
    `${item.update_kind === "delay" ? "Retard déclaré · " : item.update_kind === "budget" ? "Budget modifié · " : ""}${item.title || "Actualité du projet"}`,
    `${item.body || ""}${Number(item.delay_days) > 0 ? `\nRetard estimé : ${item.delay_days} jour(s).` : ""}${Number(item.budget_change_minor) ? `\nVariation budgétaire : ${fromMinorUnits(item.budget_change_minor, currency)} ${currency}.` : ""}`,
  ]);
  const expenses = asArray(campaign?.expenses).map((item) => ({
    id: item?.public_id,
    title: item?.title || "Dépense déclarée",
    description: item?.description || "",
    vendor: item?.vendor_label || "",
    amount: fromMinorUnits(item?.amount_minor, item?.currency || currency),
    currency: item?.currency || currency,
    occurredOn: item?.occurred_on || null,
    status: item?.review_state || "declared",
    documents: asArray(item?.public_documents),
  }));

  return {
    id: String(campaign?.public_id || campaign?.slug || index),
    publicId: campaign?.public_id ? String(campaign.public_id) : null,
    slug: campaign?.slug || "",
    source: "api",
    isDemo: String(campaign?.environment || "sandbox").toLowerCase() === "demo",
    isSandbox: String(campaign?.environment || "sandbox").toLowerCase() === "sandbox",
    transactionsAreSimulated: String(campaign?.environment || "sandbox").toLowerCase() !== "live",
    title: version.title || project.title || "Projet sans titre",
    summary: presentation && /^Annonce de démonstration fictive/.test(version.summary || project.summary || "") ? presentation.summary : version.summary || project.summary || "La présentation détaillée sera bientôt disponible.",
    story: version.story || "Le récit détaillé n’a pas encore été publié.",
    impact: terms.impact || terms.expected_impact || "Les objectifs d’impact sont décrits par le porteur dans la campagne.",
    risks: version.risks || "Les risques n’ont pas encore été détaillés dans cette version.",
    category: campaign?.category || terms.category || "Projet citoyen",
    city: discovery.city || campaign?.location_general || terms.city || terms.location || "France",
    location: campaign?.location_general || terms.location || terms.city || "Localisation non précisée",
    creator,
    creatorProvided,
    creatorInitials: creatorInitials(creator),
    creatorVerified: false,
    creatorSince: terms.creator_since || "non communiqué",
    creatorAvatar: safeImageUrl(terms.creator_avatar_url, ""),
    image: safeImageUrl(campaign?.cover_image_url || terms.cover_image_url || terms.image_url, presentation?.image || DEFAULT_PROJECT_IMAGE),
    fictionalIllustration: Boolean(presentation && !campaign?.cover_image_url && !terms.cover_image_url && !terms.image_url),
    gallery: asArray(campaign?.gallery_urls).map((item) => safeImageUrl(item, "")).filter(Boolean),
    videoUrl: safeCrowdfundingVideoUrl(campaign?.video_url),
    raised,
    goal,
    minimumContribution: fromMinorUnits(campaign?.minimum_contribution_minor, currency),
    contributionCount: Math.max(0, asNumber(metrics?.contribution_count)),
    interestCount: Math.max(0, asNumber(campaign?.interest_count)),
    isFollowed: Boolean(campaign?.is_following),
    daysLeft: daysUntil(campaign?.ends_at, statusKey),
    growth: asNumber(terms.popularity_score, asNumber(metrics?.contribution_count)),
    featured: Boolean(discovery.is_featured) || Boolean(terms.featured) || /maison\s+horizon/i.test(version.title || project.title || ""),
    region: discovery.region || "",
    editorialCategory: discovery.editorial_category || "",
    discoveryTags: asArray(discovery.tags),
    statusKey,
    status: statusMeta.label,
    statusLabel: statusMeta.label,
    statusTone: statusMeta.tone,
    canReceiveSupport: permanent ? Boolean(campaign?.donation_terms?.payment_available) && statusMeta.open : statusMeta.open,
    currency,
    isPermanentDonation: permanent,
    donationTerms: campaign?.donation_terms || null,
    closedAt: campaign?.closed_at || null,
    fundingModel: campaign?.funding_model || "donation",
    startDate: campaign?.starts_at || null,
    endDate: campaign?.ends_at || null,
    createdAt: campaign?.created_at || null,
    createdOrder: campaign?.created_at ? new Date(campaign.created_at).getTime() : -index,
    budget,
    expenses,
    expenseTotal: expenses.reduce((total, item) => total + item.amount, 0),
    timeline: normalizePairs(terms.timeline, "date", "label"),
    rewards: asArray(campaign?.rewards?.length ? campaign.rewards : version.rewards).map((reward, rewardIndex) => ({
      id: reward?.public_id || reward?.id || `${campaign?.public_id || campaign?.slug || index}-reward-${rewardIndex}`,
      amount: reward?.minimum_contribution_minor !== undefined
        ? fromMinorUnits(reward.minimum_contribution_minor, currency)
        : reward?.amount_minor !== undefined ? fromMinorUnits(reward.amount_minor, currency) : asNumber(reward?.amount),
      title: reward?.title || "Contrepartie déclarée",
      detail: reward?.detail || reward?.description || "Détail non communiqué.",
      remaining: reward?.remaining_quantity === null ? null : Math.max(0, asNumber(reward?.remaining_quantity ?? reward?.remaining ?? reward?.quantity)),
      estimatedDeliveryDate: reward?.estimated_delivery_date || null,
      shippingRequired: Boolean(reward?.shipping_required),
      shippingAmount: fromMinorUnits(reward?.shipping_amount_minor || 0, currency),
      shippingZones: asArray(reward?.shipping_zones),
      variantOptions: asArray(reward?.variant_options),
    })),
    team: normalizePairs(terms.team, "name", "role"),
    news: updates,
    lastPublishedUpdateAt: asArray(campaign?.updates).map(item => item.published_at).filter(value => value && Number.isFinite(new Date(value).getTime())).sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || null,
    faq: normalizePairs(terms.faq, "question", "answer"),
    questions: asArray(campaign?.questions).map((item) => ({
      id: item?.public_id,
      contentKind: item?.content_kind || "question",
      question: item?.body || "",
      answer: item?.answer || "",
      answeredAt: item?.answered_at || null,
      createdAt: item?.created_at || null,
    })),
    polls: asArray(campaign?.polls).map((poll) => ({
      id: poll?.public_id,
      title: poll?.title || "Sondage",
      question: poll?.question || "",
      contributorsOnly: Boolean(poll?.contributors_only),
      state: poll?.state || "closed",
      isOpen: Boolean(poll?.is_open),
      voteCount: Math.max(0, asNumber(poll?.vote_count)),
      myVote: poll?.my_vote || null,
      closesAt: poll?.closes_at || null,
      options: asArray(poll?.options).map((option) => ({
        id: option?.public_id,
        label: option?.label || "Choix",
        voteCount: Math.max(0, asNumber(option?.vote_count)),
      })),
    })),
    stretchGoals: asArray(campaign?.stretch_goals).map((goal) => ({
      id: goal?.public_id,
      thresholdPercent: Math.max(101, asNumber(goal?.threshold_percent, 101)),
      title: goal?.title || "Objectif supplémentaire",
      description: goal?.description || "",
      unlocked: Boolean(goal?.unlocked),
    })),
    metrics,
    progressBasisPoints: asNumber(
      metrics?.raw_progress_basis_points,
      goal > 0 ? (raised / goal) * 10000 : 0
    ),
  };
}

export function deriveCrowdfundingStats(projects) {
  const items = asArray(projects);
  return items.reduce(
    (stats, project) => {
      const status = String(project?.statusKey || "");
      const goal = Math.max(0, asNumber(project?.goal));
      const raised = Math.max(0, asNumber(project?.raised));
      const currency = String(project?.currency || "EUR").toUpperCase();
      if (["live", "active", "scheduled"].includes(status)) stats.activeProjects += 1;
      stats.raisedByCurrency[currency] = (stats.raisedByCurrency[currency] || 0) + raised;
      stats.supportCount += Math.max(0, asNumber(project?.contributionCount ?? project?.contributors));
      if (["succeeded", "successful"].includes(status) || (goal > 0 && raised >= goal)) stats.fundedProjects += 1;
      return stats;
    },
    { activeProjects: 0, raisedByCurrency: {}, supportCount: 0, fundedProjects: 0 }
  );
}

export async function loadCrowdfundingCatalog({ signal, filters = {} } = {}) {
  const capabilitiesResponse = await api.get("/api/crowdfunding/capabilities/", { signal });
  const capabilities = {
    ...CLOSED_CROWDFUNDING_CAPABILITIES,
    ...(capabilitiesResponse?.data || {}),
  };

  if (!capabilities.campaign_read_enabled) {
    return {
      capabilities,
      projects: [],
      source: "api",
      fallbackReason: "hub_closed",
    };
  }

  const activeFilters = Object.fromEntries(
    Object.entries(filters || {}).filter(([, value]) => value !== "" && value !== null && value !== undefined)
  );
  const requestConfig = Object.keys(activeFilters).length
    ? { signal, params: { page_size: 60, ...activeFilters } }
    : { signal };
  const campaignsResponse = await api.get("/api/crowdfunding/campaigns/", requestConfig);
  const campaigns = unwrapCollection(campaignsResponse?.data);
  return {
    capabilities,
    projects: campaigns.map(normalizeApiCampaign),
    source: "api",
    fallbackReason: "",
  };
}

export async function loadCrowdfundingCampaign(publicId, { signal } = {}) {
  const response = await api.get(
    `/api/crowdfunding/campaigns/${encodeURIComponent(publicId)}/`,
    { signal }
  );
  return normalizeApiCampaign(response?.data || {});
}

export async function beginCrowdfundingSupport(publicId, payload, idempotencyKey) {
  return api.post(
    `/api/crowdfunding/campaigns/${encodeURIComponent(publicId)}/checkout/`,
    payload,
    { headers: { "Idempotency-Key": idempotencyKey } }
  );
}

export async function loadMyCrowdfundingContributions({ signal } = {}) {
  const response = await api.get("/api/crowdfunding/me/contributions/", { signal });
  return unwrapCollection(response?.data);
}

export async function loadMyCrowdfundingCampaigns({ signal } = {}) {
  const response = await api.get("/api/crowdfunding/me/campaigns/", { signal });
  return unwrapCollection(response?.data).map(normalizeApiCampaign);
}

export async function createCrowdfundingCampaign(payload) {
  const response = await api.post("/api/crowdfunding/campaigns/", payload);
  return response?.data;
}

export async function autosaveCrowdfundingCampaign(publicId, payload) {
  const response = await api.patch(
    `/api/crowdfunding/me/campaigns/${encodeURIComponent(publicId)}/`,
    payload
  );
  return response?.data;
}

export async function submitCrowdfundingCampaign(publicId) {
  const response = await api.post(
    `/api/crowdfunding/me/campaigns/${encodeURIComponent(publicId)}/submit/`,
    { terms_accepted: true }
  );
  return response?.data;
}

export async function setCrowdfundingFollow(publicId, enabled, preferences = {}) {
  const url = `/api/crowdfunding/campaigns/${encodeURIComponent(publicId)}/follow/`;
  if (!enabled) return api.delete(url);
  return api.post(url, {
    notify_launch: preferences.notifyLaunch !== false,
    notify_updates: preferences.notifyUpdates !== false,
    notify_deadline: preferences.notifyDeadline !== false,
  });
}

export async function trackCrowdfundingEvent(publicId, eventType, { referralCode = "", visitorToken = "", idempotencyKey, source = "", medium = "", campaignTag = "" } = {}) {
  const search = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const width = typeof window !== "undefined" ? window.innerWidth : 0;
  return api.post(`/api/crowdfunding/campaigns/${encodeURIComponent(publicId)}/track/`, {
    event_type: eventType,
    idempotency_key: idempotencyKey || `crowdfunding-${eventType}-${publicId}-${Date.now()}`,
    visitor_token: visitorToken,
    referral_code: referralCode,
    source: source || search.get("utm_source") || (typeof document !== "undefined" && document.referrer ? "referral" : "direct"),
    medium: medium || search.get("utm_medium") || "web",
    campaign_tag: campaignTag || search.get("utm_campaign") || "",
    device_kind: width ? (width < 640 ? "mobile" : width < 1024 ? "tablet" : "desktop") : "unknown",
    landing_path: typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}`.slice(0, 300) : "",
  });
}

export async function askCrowdfundingQuestion(publicId, body, contentKind = "question") {
  const response = await api.post(
    `/api/crowdfunding/campaigns/${encodeURIComponent(publicId)}/questions/`,
    { body, content_kind: contentKind }
  );
  return response?.data;
}

export async function voteCrowdfundingPoll(publicId, pollId, optionId) {
  const response = await api.post(
    `/api/crowdfunding/campaigns/${encodeURIComponent(publicId)}/vote/`,
    { poll_id: pollId, option_id: optionId }
  );
  return response?.data;
}

export async function loadCrowdfundingStudio(publicId, { signal } = {}) {
  const response = await api.get(
    `/api/crowdfunding/me/campaigns/${encodeURIComponent(publicId)}/studio/`,
    { signal }
  );
  return response?.data;
}

export async function publishCrowdfundingUpdate(publicId, payload) {
  const response = await api.post(
    `/api/crowdfunding/me/campaigns/${encodeURIComponent(publicId)}/updates/`,
    payload
  );
  return response?.data;
}

export async function declareCrowdfundingExpense(publicId, payload, idempotencyKey) {
  const response = await api.post(
    `/api/crowdfunding/me/campaigns/${encodeURIComponent(publicId)}/expenses/`,
    payload,
    { headers: { "Idempotency-Key": idempotencyKey } }
  );
  return response?.data;
}

export async function uploadCrowdfundingProof(file, title) {
  const form = new FormData();
  form.append("file", file);
  form.append("title", title || file?.name || "Justificatif crowdfunding");
  form.append("category", "other");
  const response = await api.post("/api/me/document-vault/uploads/", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response?.data;
}

export async function replaceCrowdfundingStretchGoals(publicId, goals) {
  const response = await api.put(
    `/api/crowdfunding/me/campaigns/${encodeURIComponent(publicId)}/stretch-goals/`,
    { goals }
  );
  return response?.data;
}

export async function createCrowdfundingPoll(publicId, payload) {
  const response = await api.post(
    `/api/crowdfunding/me/campaigns/${encodeURIComponent(publicId)}/polls/`,
    payload
  );
  return response?.data;
}

export async function moderateCrowdfundingQuestion(publicId, payload) {
  const response = await api.post(
    `/api/crowdfunding/me/campaigns/${encodeURIComponent(publicId)}/moderate-question/`,
    payload
  );
  return response?.data;
}

export async function createCrowdfundingReferralLink(publicId, payload = {}) {
  const response = await api.post(
    `/api/crowdfunding/me/campaigns/${encodeURIComponent(publicId)}/referral-links/`,
    payload
  );
  return response?.data;
}

export async function loadCrowdfundingFulfillment(contributionId, { signal } = {}) {
  const response = await api.get(
    `/api/crowdfunding/me/contributions/${encodeURIComponent(contributionId)}/fulfillment/`,
    { signal }
  );
  return response?.data;
}

export async function updateCrowdfundingFulfillment(contributionId, payload) {
  const response = await api.patch(
    `/api/crowdfunding/me/contributions/${encodeURIComponent(contributionId)}/fulfillment/`,
    payload
  );
  return response?.data;
}

export async function loadCreatorCrowdfundingFulfillments(publicId, { signal } = {}) {
  const response = await api.get(
    `/api/crowdfunding/me/campaigns/${encodeURIComponent(publicId)}/fulfillments/`,
    { signal }
  );
  return response?.data || [];
}

export async function updateCreatorCrowdfundingFulfillment(publicId, payload) {
  const response = await api.patch(
    `/api/crowdfunding/me/campaigns/${encodeURIComponent(publicId)}/fulfillments/`,
    payload
  );
  return response?.data;
}

const operationsUrl = (publicId, action) => `/api/crowdfunding/me/operations/${encodeURIComponent(publicId)}/${action}/`;
const publicOperationsUrl = (publicId, action) => `/api/crowdfunding/operations/campaigns/${encodeURIComponent(publicId)}/${action}/`;

export async function loadCrowdfundingOperations(publicId, { signal } = {}) {
  const responses = await Promise.allSettled([
    api.get(operationsUrl(publicId, "analytics-advanced"), { signal }),
    api.get(operationsUrl(publicId, "conversations"), { signal }),
    api.get(operationsUrl(publicId, "variant-stock"), { signal }),
    api.get(operationsUrl(publicId, "communications"), { signal }),
    api.get(operationsUrl(publicId, "discovery"), { signal }),
    api.get(operationsUrl(publicId, "batches"), { signal }),
  ]);
  const value = (index, fallback) => responses[index].status === "fulfilled" ? responses[index].value.data : fallback;
  return {
    analytics: value(0, {}),
    conversations: value(1, []),
    stock: value(2, []),
    communications: value(3, []),
    discovery: value(4, {}),
    batches: value(5, []),
  };
}

export async function sendCreatorCrowdfundingMessage(publicId, payload) {
  return (await api.post(operationsUrl(publicId, "messages"), payload)).data;
}

export async function sendCrowdfundingGroupMessage(publicId, payload) {
  return (await api.post(operationsUrl(publicId, "group-message"), { ...payload, confirmed: true })).data;
}

export async function saveCrowdfundingMessageTemplate(publicId, payload) {
  return (await api.post(operationsUrl(publicId, "templates"), payload)).data;
}

export async function saveCrowdfundingDiscovery(publicId, payload) {
  return (await api.put(operationsUrl(publicId, "discovery"), payload)).data;
}

export async function saveCrowdfundingCommunication(publicId, payload) {
  return (await api.post(operationsUrl(publicId, "communications"), payload)).data;
}

export async function saveCrowdfundingVariantStock(publicId, payload) {
  return (await api.post(operationsUrl(publicId, "variant-stock"), payload)).data;
}

export async function createCrowdfundingPreparationBatch(publicId, payload) {
  return (await api.post(operationsUrl(publicId, "batches"), payload)).data;
}

export async function importCrowdfundingFulfillmentsCsv(publicId, csv) {
  return (await api.post(operationsUrl(publicId, "fulfillments-csv"), { csv })).data;
}

export async function exportCrowdfundingCsv(publicId, kind) {
  const action = kind === "analytics" ? "analytics-export" : "fulfillments-csv";
  return (await api.get(operationsUrl(publicId, action), { responseType: "text" })).data;
}

export async function reportCrowdfundingCampaign(publicId, payload) {
  return (await api.post(publicOperationsUrl(publicId, "report"), payload)).data;
}

export async function loadCrowdfundingRecommendations(publicId, { signal } = {}) {
  const response = await api.get(publicOperationsUrl(publicId, "recommendations"), { signal });
  return asArray(response.data).map(normalizeApiCampaign);
}

export async function ensureCrowdfundingConversation(publicId, subject = "") {
  return (await api.post(publicOperationsUrl(publicId, "conversation"), { subject })).data;
}

export async function sendContributorCrowdfundingMessage(publicId, conversationId, body) {
  return (await api.post(publicOperationsUrl(publicId, "messages"), {
    conversation_id: conversationId,
    body,
  })).data;
}

export async function loadMyCrowdfundingMessages({ signal } = {}) {
  return (await api.get("/api/crowdfunding/me/messages/", { signal })).data || [];
}

export async function loadCrowdfundingLegal(publicId, { signal } = {}) {
  return (await api.get(publicOperationsUrl(publicId, "legal"), { signal })).data || [];
}

export async function acceptCrowdfundingLegal(publicId, documentId) {
  return (await api.post(publicOperationsUrl(publicId, "legal"), { document_id: documentId, confirmed: true })).data;
}

const ecosystemUrl = (publicId, action) => `/api/crowdfunding/me/ecosystem/${encodeURIComponent(publicId)}/${action}/`;

export async function loadCrowdfundingEcosystemDashboard(publicId, { signal } = {}) {
  return (await api.get(ecosystemUrl(publicId, "dashboard"), { signal })).data;
}

export async function runCrowdfundingEcosystemAction(publicId, action, payload = {}) {
  return (await api.post(ecosystemUrl(publicId, action), payload)).data;
}

export async function loadCrowdfundingVersionComparison(publicId, fromVersion, toVersion) {
  return (await api.get(ecosystemUrl(publicId, "version-comparison"), {
    params: { from: fromVersion, to: toVersion },
  })).data;
}

export async function loadPublicCrowdfundingEcosystem(publicId, locale = "", { signal } = {}) {
  return (await api.get(
    `/api/crowdfunding/ecosystem/campaigns/${encodeURIComponent(publicId)}/details/`,
    { params: locale ? { locale } : {}, signal }
  )).data;
}

export async function loadCrowdfundingArchives({ signal } = {}) {
  return (await api.get("/api/crowdfunding/ecosystem/campaigns/archives/", { signal })).data || [];
}

export async function loadMyCrowdfundingSupport({ signal } = {}) {
  return (await api.get("/api/crowdfunding/me/support/", { signal })).data || [];
}

export async function openCrowdfundingSupportTicket(payload) {
  return (await api.post("/api/crowdfunding/me/support/", payload)).data;
}

export async function sendCrowdfundingSupportMessage(ticketId, body) {
  return (await api.post("/api/crowdfunding/me/support/messages/", { ticket_id: ticketId, body })).data;
}

export async function acceptCrowdfundingCollaboration(token) {
  return (await api.post("/api/crowdfunding/ecosystem/collaborations/accept/", { token })).data;
}

export async function loadCrowdfundingPreview(token, locale = "", { signal } = {}) {
  return (await api.get(`/api/crowdfunding/ecosystem/preview/${encodeURIComponent(token)}/`, {
    params: locale ? { locale } : {}, signal,
  })).data;
}

export async function loadCrowdfundingAdminEcosystem({ signal } = {}) {
  return (await api.get("/api/crowdfunding/admin/ecosystem/", { signal })).data;
}

export async function loadCrowdfundingAdminSupport({ signal } = {}) {
  return (await api.get("/api/crowdfunding/admin/ecosystem/support/", { signal })).data || [];
}

export async function reviewCrowdfundingAdminSupport(payload) {
  return (await api.post("/api/crowdfunding/admin/ecosystem/support/", payload)).data;
}

export async function loadCrowdfundingAdminVerifications({ signal } = {}) {
  return (await api.get("/api/crowdfunding/admin/ecosystem/verifications/", { signal })).data || [];
}

export async function reviewCrowdfundingAdminVerification(payload) {
  return (await api.post("/api/crowdfunding/admin/ecosystem/verifications/", payload)).data;
}

export async function loadCrowdfundingVerificationDocument(verificationId) {
  return (await api.post("/api/crowdfunding/admin/ecosystem/verification-document/", {
    verification_id: verificationId,
    confirmed: true,
  })).data;
}

const growthUrl = (publicId, action) => `/api/crowdfunding/me/growth/${encodeURIComponent(publicId)}/${action}/`;

export async function loadCrowdfundingGrowthDashboard(publicId, { signal } = {}) {
  return (await api.get(growthUrl(publicId, "dashboard"), { signal })).data;
}

export async function runCrowdfundingGrowthAction(publicId, action, payload = {}) {
  return (await api.post(growthUrl(publicId, action), payload)).data;
}

export async function exportCrowdfundingAccountingScenario(publicId, scenarioId) {
  return (await api.get(growthUrl(publicId, "accounting-export"), {
    params: { scenario_id: scenarioId }, responseType: "blob",
  })).data;
}

export async function loadPublicCrowdfundingGrowth(publicId, { signal } = {}) {
  return (await api.get(`/api/crowdfunding/growth/campaigns/${encodeURIComponent(publicId)}/details/`, { signal })).data;
}

export async function assignCrowdfundingExperiment(publicId, experimentId, visitorToken) {
  return (await api.post(`/api/crowdfunding/growth/campaigns/${encodeURIComponent(publicId)}/experiment-assignment/`, {
    experiment_id: experimentId, visitor_token: visitorToken,
  })).data;
}

export async function trackCrowdfundingExperiment(publicId, assignmentId, eventType, visitorToken) {
  return (await api.post(`/api/crowdfunding/growth/campaigns/${encodeURIComponent(publicId)}/experiment-event/`, {
    assignment_id: assignmentId, event_type: eventType, visitor_token: visitorToken,
  })).data;
}

export async function updateCrowdfundingCrmConsent(payload) {
  return (await api.post("/api/crowdfunding/me/growth-actions/consent/", payload)).data;
}

export async function voteCrowdfundingGovernance(proposalId, choiceId) {
  return (await api.post("/api/crowdfunding/me/growth-actions/vote/", { proposal_id: proposalId, choice_id: choiceId })).data;
}

export async function saveCrowdfundingSandboxProgress(templateId, completedSteps) {
  return (await api.post("/api/crowdfunding/me/growth-actions/sandbox-progress/", { template_id: templateId, completed_steps: completedSteps })).data;
}

export async function scoreCrowdfundingProgramApplication(payload) {
  return (await api.post("/api/crowdfunding/me/growth-actions/jury-score/", payload)).data;
}

export async function loadCrowdfundingImpactRegistry({ signal } = {}) {
  return (await api.get("/api/crowdfunding/growth/campaigns/registry/", { signal })).data || [];
}

export async function loadCrowdfundingPrograms({ signal } = {}) {
  return (await api.get("/api/crowdfunding/growth/campaigns/programs/", { signal })).data || [];
}

export async function loadCrowdfundingSandboxTemplates({ signal } = {}) {
  return (await api.get("/api/crowdfunding/growth/campaigns/sandbox-templates/", { signal })).data || [];
}

export async function loadCrowdfundingBrand(slug, { signal } = {}) {
  return (await api.get("/api/crowdfunding/growth/campaigns/brand/", { params: { slug }, signal })).data;
}

export async function loadMyCrowdfundingPartners({ signal } = {}) {
  return (await api.get("/api/crowdfunding/partners/growth/", { signal })).data || [];
}

export async function loadCrowdfundingPartnerDashboard(organisationId, { signal } = {}) {
  return (await api.get(`/api/crowdfunding/partners/growth/${encodeURIComponent(organisationId)}/dashboard/`, { signal })).data;
}

export async function loadCrowdfundingAdminGrowth({ signal } = {}) {
  return (await api.get("/api/crowdfunding/admin/growth/", { signal })).data;
}

export async function loadCrowdfundingAdminGrowthSection(section, { signal } = {}) {
  return (await api.get(`/api/crowdfunding/admin/growth/${section}/`, { signal })).data || [];
}

export async function runCrowdfundingAdminGrowthAction(section, payload = {}) {
  return (await api.post(`/api/crowdfunding/admin/growth/${section}/`, payload)).data;
}

const maturityUrl = (publicId, action) => `/api/crowdfunding/me/maturity/${encodeURIComponent(publicId)}/${action}/`;

const solidarityUrl = (publicId, action = "") => `/api/crowdfunding/solidarity/campaigns/${encodeURIComponent(publicId)}/${action ? `${action}/` : ""}`;
export async function loadCrowdfundingSolidarity(publicId, { signal } = {}) {
  return (await api.get(solidarityUrl(publicId), { signal })).data;
}
export async function runCrowdfundingSolidarity(publicId, action, payload = {}) {
  return (await api.post(solidarityUrl(publicId, action), payload)).data;
}
export async function loadCrowdfundingResourceDirectory(params = {}, { signal } = {}) {
  return (await api.get("/api/crowdfunding/solidarity/campaigns/", { params, signal })).data;
}
export async function loadMyCrowdfundingSolidarity({ signal } = {}) {
  return (await api.get("/api/crowdfunding/me/solidarity/", { signal })).data;
}
export async function runCrowdfundingTransfer(payload) {
  return (await api.post("/api/crowdfunding/me/solidarity/transfer-action/", payload)).data;
}

const communityUrl = (publicId, action = "") => `/api/crowdfunding/community/campaigns/${encodeURIComponent(publicId)}/${action ? `${action}/` : ""}`;
export async function loadCrowdfundingCommunity(publicId, { signal } = {}) {
  return (await api.get(communityUrl(publicId), { signal })).data;
}
export async function runCrowdfundingCommunity(publicId, command, payload = {}) {
  return (await api.post(communityUrl(publicId, "command"), { command, payload })).data;
}
export async function loadCrowdfundingCommunityDirectory(params = {}, { signal } = {}) {
  return (await api.get("/api/crowdfunding/community/campaigns/", { params, signal })).data;
}

export async function loadCrowdfundingMaturityDashboard(publicId, { signal } = {}) {
  return (await api.get(maturityUrl(publicId, "dashboard"), { signal })).data;
}

export async function runCrowdfundingMaturityAction(publicId, action, payload = {}) {
  return (await api.post(maturityUrl(publicId, action), payload)).data;
}

export async function exportCrowdfundingPortableData(publicId, exportFormat = "json") {
  return (await api.post(maturityUrl(publicId, "portability"), { export_format: exportFormat }, { responseType: "blob" })).data;
}

export async function loadCrowdfundingGuidedTours({ signal } = {}) {
  return (await api.get("/api/crowdfunding/maturity/public/tours/", { signal })).data;
}

export async function loadCrowdfundingCertificationCourse({ signal } = {}) {
  return (await api.get("/api/crowdfunding/maturity/public/certification/", { signal })).data;
}

export async function saveCrowdfundingGuidedTourProgress(roleKey, completedSteps) {
  return (await api.post("/api/crowdfunding/me/maturity-actions/tour-progress/", { role_key: roleKey, completed_steps: completedSteps })).data;
}

export async function loadCrowdfundingAdminMaturity({ signal } = {}) {
  return (await api.get("/api/crowdfunding/admin/maturity/", { signal })).data;
}

export async function runCrowdfundingAdminMaturityAction(action, payload = {}) {
  return (await api.post(`/api/crowdfunding/admin/maturity/${action}/`, payload)).data;
}
